import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ResponsiveImage } from "~/components/ui/responsive-image";
import { FieldComponentProps } from "~/lib/form";
import { cn } from "~/shared/lib/utils";
import { t } from "@lingui/core/macro";

interface ValidatedFileUploadProps extends FieldComponentProps {
  accept?: string;
  maxSizeMb?: number;
  description?: string;
  helperText?: string;
  previewAlt?: string;
}

export function ValidatedFileUpload(props: ValidatedFileUploadProps) {
  const {
    field,
    label,
    placeholder,
    className,
    accept = "image/*",
    maxSizeMb = 5,
    description,
    helperText,
    previewAlt = "Uploaded file preview",
  } = props;

  const inputId = `${field.name}-${useId()}-file-input`;
  const meta = field.state.meta;
  const currentValue = field.state.value as File | string | null | undefined;

  const [sizeError, setSizeError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const objectUrl = useMemo(() => {
    if (currentValue instanceof File) {
      return URL.createObjectURL(currentValue);
    }
    if (typeof currentValue === "string" && currentValue.length > 0) {
      return currentValue;
    }
    return null;
  }, [currentValue]);

  useEffect(() => {
    const revoke =
      typeof URL.revokeObjectURL === "function" ? URL.revokeObjectURL.bind(URL) : null;
    if (!revoke) return undefined;

    if (currentValue instanceof File && objectUrl) {
      if (objectUrlRef.current && objectUrlRef.current !== objectUrl) {
        revoke(objectUrlRef.current);
      }
      objectUrlRef.current = objectUrl;
    } else if (objectUrlRef.current) {
      revoke(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    return () => {
      if (objectUrlRef.current) {
        revoke(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [currentValue, objectUrl]);

  useEffect(() => {
    // Reset preview-loading flag whenever the image preview changes.
    // oxlint-disable-next-line eslint-react-js/hooks-extra/no-direct-set-state-in-use-effect -- state sync with external source; useRef gating prevents loops.
    // oxlint-disable-next-line eslint-react-js/hooks-extra/no-direct-set-state-in-use-effect -- resetting preview-loading when upstream preview source changes.
    setPreviewLoading(Boolean(accept.startsWith("image") && objectUrl));
    // oxlint-disable-next-line eslint-react-js/hooks-extra/no-direct-set-state-in-use-effect -- state sync with external source; useRef gating prevents loops.
  }, [accept, objectUrl]);

  const humanReadableSize = useMemo(() => {
    if (currentValue instanceof File) {
      return formatFileSize(currentValue.size);
    }
    return null;
  }, [currentValue]);

  return (
    <div className={cn("space-y-3", className)}>
      <Label htmlFor={inputId}>{label}</Label>
      <div className="flex flex-col gap-3">
        <Input
          id={inputId}
          type="file"
          accept={accept}
          placeholder={placeholder}
          onBlur={field.handleBlur}
          disabled={field.form.state.isSubmitting}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (!file) {
              field.handleChange(null);
              setSizeError(null);
              return;
            }

            if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
              setSizeError(`File is too large. Maximum size is ${maxSizeMb}MB.`);
              event.target.value = "";
              field.handleChange(null);
              return;
            }

            setSizeError(null);
            field.handleChange(file);
          }}
        />
        {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
        {sizeError && (
          <p
            role="alert"
            aria-live="assertive"
            className="text-destructive text-sm font-medium"
          >
            {label}: {sizeError}
          </p>
        )}

        {objectUrl && (
          <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4">
            <p className="text-sm font-semibold text-foreground">{t`Selected file`}</p>
            <p className="text-xs text-muted-foreground">
              {currentValue instanceof File ? currentValue.name : "Existing upload"}
              {humanReadableSize ? ` · ${humanReadableSize}` : ""}
            </p>
            {accept.startsWith("image") && (
              <>
                {previewLoading && (
                  <div className="mt-3 rounded-md border border-dashed border-border bg-white/60 p-4 text-center text-xs text-muted-foreground">
                    {t`Loading preview...`}
                  </div>
                )}
                <ResponsiveImage
                  src={objectUrl}
                  alt={previewAlt}
                  className={cn(
                    "mt-3 max-h-48 w-full rounded-md object-cover",
                    previewLoading && "opacity-0",
                  )}
                  loading="lazy"
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => setPreviewLoading(false)}
                />
              </>
            )}
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  field.handleChange(null);
                  setSizeError(null);
                }}
              >
                {t`Remove file`}
              </Button>
            </div>
          </div>
        )}
      </div>
      {description && <p className="text-muted-foreground text-sm">{description}</p>}
      {meta.isTouched && meta.errors.length > 0 && (
        <div
          id={`${inputId}-errors`}
          role="alert"
          aria-live="polite"
          className="text-destructive text-sm font-medium"
        >
          {label}: {meta.errors.join(", ")}
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
